// Dashboard-wide pure utilities. Kept dependency-free so they can be
// imported anywhere without dragging React along.

export const DISPENSING_FEE = 6.83; // Per-dispensing professional fee for zero-fee recovery estimate
export const COPAY_WAIVED_PER_DISPENSING = 2.0; // $2 patient deductible waived on each paid dispensing

export const ODB_MONTH_SHORT = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

export function formatCurrency(n) {
  return new Intl.NumberFormat("en-CA", {
    style: "currency",
    currency: "CAD",
    currencyDisplay: "narrowSymbol",
    maximumFractionDigits: 2,
  }).format(n || 0);
}

export function clampIso(iso, min, max) {
  if (!iso) return iso;
  if (min && iso < min) return min;
  if (max && iso > max) return max;
  return iso;
}

export function todayIso() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export function lastDayOfMonth(ym) {
  const [y, m] = ym.split("-").map(Number);
  return `${ym}-${String(new Date(y, m, 0).getDate()).padStart(2, "0")}`;
}

// Resolve the rebate amount for a given month under the current product scope.
// Rebates are stored as either a plain number (legacy, treated as Non-HCV) OR
// as { hcv: number, nonHcv: number } so the two product scopes can carry
// separate rebate cheques.
export function rebateAmountFor(rebates, ym, viewMode) {
  const r = rebates?.[ym];
  if (r == null) return 0;
  if (typeof r === "number") return viewMode === "non-hcv" ? r : 0;
  return viewMode === "hcv" ? (r.hcv || 0) : (r.nonHcv || 0);
}

export function addMonths(ym, delta) {
  const [y, m] = ym.split("-").map(Number);
  const total = y * 12 + (m - 1) + delta;
  const ny = Math.floor(total / 12);
  const nm = (total % 12) + 1;
  return `${ny}-${String(nm).padStart(2, "0")}`;
}

export function isoFromYmd(y, m, d) {
  return `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
}

export function monthShort(ym) {
  const [y, m] = ym.split("-").map(Number);
  return `${ODB_MONTH_SHORT[m - 1]} ${y}`;
}

// ODB cycle helpers — Ontario Drug Benefit reimbursement cadence.
//   Cycle A (mid-month):  dispensings  9–22 of month M → deposit on (M+1) 15th
//   Cycle B (end/start):  dispensings  23 of M to  8 of (M+1) → deposit on (M+1) 30th
// Generate all ODB cycles that touch [reportStart, reportEnd]. Cycles are
// returned newest-first to match how the user thinks about deposits.
export function odbCyclesInRange(reportStart, reportEnd) {
  if (!reportStart || !reportEnd) return [];
  const startYm = reportStart.slice(0, 7);
  const endYm = reportEnd.slice(0, 7);
  const months = [];
  let cur = addMonths(startYm, -1);
  // Include one extra month after endYm so a Cycle B that started in endYm
  // still gets its payDate (which lands in the following month).
  const stopYm = addMonths(endYm, 1);
  while (cur <= stopYm) {
    months.push(cur);
    cur = addMonths(cur, 1);
  }
  const out = [];
  for (const ym of months) {
    const [y, m] = ym.split("-").map(Number);
    const nextYm = addMonths(ym, 1);
    const [ny, nm] = nextYm.split("-").map(Number);
    // Cycle A — 9 to 22
    const aStart = isoFromYmd(y, m, 9);
    const aEnd = isoFromYmd(y, m, 22);
    const aPay = isoFromYmd(ny, nm, 15);
    if (!(aEnd < reportStart || aStart > reportEnd)) {
      out.push({
        key: `${ym}-A`,
        label: `${ODB_MONTH_SHORT[m - 1]} ${y} · 9–22`,
        shortLabel: `${ODB_MONTH_SHORT[m - 1]} 9–22`,
        type: "A",
        dispenseStart: aStart,
        dispenseEnd: aEnd,
        payDate: aPay,
      });
    }
    // Cycle B — 23 of M to 8 of M+1
    const bStart = isoFromYmd(y, m, 23);
    const bEnd = isoFromYmd(ny, nm, 8);
    const bPay = isoFromYmd(ny, nm, 30);
    if (!(bEnd < reportStart || bStart > reportEnd)) {
      out.push({
        key: `${ym}-B`,
        label: `${ODB_MONTH_SHORT[m - 1]} ${y} · 23 - ${ODB_MONTH_SHORT[nm - 1]} 8`,
        shortLabel: `${ODB_MONTH_SHORT[m - 1]} 23 - ${ODB_MONTH_SHORT[nm - 1]} 8`,
        type: "B",
        dispenseStart: bStart,
        dispenseEnd: bEnd,
        payDate: bPay,
      });
    }
  }
  return out.sort((a, b) => b.dispenseStart.localeCompare(a.dispenseStart));
}

// Pretty-format a YYYY-MM-DD as "Mon 15, 2026"
export function formatPayDate(iso) {
  if (!iso) return "";
  const [y, m, d] = iso.split("-").map(Number);
  return `${ODB_MONTH_SHORT[m - 1]} ${d}, ${y}`;
}

// YYYY-MM-DD -> "May 09"
export function formatShortDate(iso) {
  if (!iso) return "";
  const [, m, d] = iso.split("-");
  return `${ODB_MONTH_SHORT[parseInt(m, 10) - 1]} ${parseInt(d, 10).toString().padStart(2, "0")}`;
}

// ---------------------------------------------------------------------
// NIHB cycle helpers — bi-weekly. Every cycle covers a 14-day claim
// submission window that runs Friday → Thursday (submission cutoff is
// the prior Thursday), with the deposit landing the following
// Wednesday (6 days after the cutoff). Anchored to the user-supplied
// cycle Jul 10 → Jul 23 → pay Jul 29 and extrapolated in both
// directions so historical PDFs still resolve cleanly.
// ---------------------------------------------------------------------
const NIHB_ANCHOR_START = "2026-07-10"; // Friday cycle start
const NIHB_CYCLE_DAYS = 14;
const NIHB_PAY_OFFSET_DAYS = 6;

function daysBetween(a, b) {
  const [ay, am, ad] = a.split("-").map(Number);
  const [by, bm, bd] = b.split("-").map(Number);
  const da = Date.UTC(ay, am - 1, ad);
  const db = Date.UTC(by, bm - 1, bd);
  return Math.round((db - da) / 86400000);
}

function addDaysIso(iso, days) {
  const [y, m, d] = iso.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d + days));
  return dt.toISOString().slice(0, 10);
}

function nihbCycleAtIndex(idx) {
  const start = addDaysIso(NIHB_ANCHOR_START, idx * NIHB_CYCLE_DAYS);
  const end = addDaysIso(start, NIHB_CYCLE_DAYS - 1);
  const pay = addDaysIso(end, NIHB_PAY_OFFSET_DAYS);
  const [, sm, sd] = start.split("-").map(Number);
  const [, em, ed] = end.split("-").map(Number);
  return {
    key: `nihb-${start}`,
    // Human labels shaped like ODB cycles so drop-in rendering works.
    // Use a plain hyphen (jsPDF Helvetica has no glyph for → / – so
    // those characters render as garbled wide-tracked fallbacks in
    // the exported PDF).
    label: `${ODB_MONTH_SHORT[sm - 1]} ${sd} - ${ODB_MONTH_SHORT[em - 1]} ${ed}`,
    shortLabel: `${ODB_MONTH_SHORT[sm - 1]} ${sd} - ${ODB_MONTH_SHORT[em - 1]} ${ed}`,
    type: "N",
    dispenseStart: start,
    dispenseEnd: end,
    payDate: pay,
  };
}

// Return the NIHB cycle a given service date falls in (or null on
// missing input). Used by exports + drill-downs to bucket a claim.
export function nihbCycleForServiceDate(iso) {
  if (!iso) return null;
  const delta = daysBetween(NIHB_ANCHOR_START, iso);
  const idx = Math.floor(delta / NIHB_CYCLE_DAYS);
  return nihbCycleAtIndex(idx);
}

// Return every NIHB cycle whose 14-day window overlaps [reportStart,
// reportEnd]. Sorted newest-first (dispense window descending) to
// match how ODB cycles are ordered.
export function nihbCyclesInRange(reportStart, reportEnd) {
  if (!reportStart || !reportEnd) return [];
  const startDelta = daysBetween(NIHB_ANCHOR_START, reportStart);
  const endDelta = daysBetween(NIHB_ANCHOR_START, reportEnd);
  const firstIdx = Math.floor(startDelta / NIHB_CYCLE_DAYS);
  const lastIdx = Math.floor(endDelta / NIHB_CYCLE_DAYS);
  const out = [];
  for (let i = firstIdx; i <= lastIdx; i++) {
    out.push(nihbCycleAtIndex(i));
  }
  return out.sort((a, b) => b.dispenseStart.localeCompare(a.dispenseStart));
}
